const ATTRIBUTION_STORAGE_KEY = "sippulse.signup-attribution";
const SIGNUP_METHOD_STORAGE_KEY = "sippulse.signup-method";
const ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000;

const attributionKeys = [
  "gclid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_content_lp",
] as const;

type AttributionKey = (typeof attributionKeys)[number];
export type SignupAttribution = Partial<Record<AttributionKey, string>>;
export type SignupMethod = "email" | "google" | "microsoft";

interface StoredAttribution {
  attribution: SignupAttribution;
  expiresAt: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function removeStoredAttribution() {
  try {
    localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
  } catch {
    // Storage may be unavailable in privacy-restricted browser contexts.
  }
}

function removeStoredSignupMethod() {
  try {
    sessionStorage.removeItem(SIGNUP_METHOD_STORAGE_KEY);
  } catch {
    // Storage may be unavailable in privacy-restricted browser contexts.
  }
}

export function captureSignupAttribution(search = globalThis.location.search) {
  const searchParams = new URLSearchParams(search);
  const attribution: SignupAttribution = {};

  for (const key of attributionKeys) {
    const value = searchParams.get(key)?.trim();
    if (value) attribution[key] = value;
  }

  if (Object.keys(attribution).length === 0) return;

  const stored: StoredAttribution = {
    attribution,
    expiresAt: Date.now() + ATTRIBUTION_TTL_MS,
  };

  try {
    localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Attribution must never block the registration flow.
  }
}

export function getSignupAttribution(): SignupAttribution {
  try {
    const raw = localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return {};

    const stored: unknown = JSON.parse(raw);
    if (
      !isRecord(stored) ||
      typeof stored.expiresAt !== "number" ||
      Date.now() > stored.expiresAt
    ) {
      removeStoredAttribution();
      return {};
    }

    if (!isRecord(stored.attribution)) {
      removeStoredAttribution();
      return {};
    }

    const attribution: SignupAttribution = {};
    for (const key of attributionKeys) {
      const value = stored.attribution[key];
      if (typeof value === "string" && value) attribution[key] = value;
    }

    return attribution;
  } catch {
    removeStoredAttribution();
    return {};
  }
}

export function setSignupMethod(method: SignupMethod) {
  try {
    sessionStorage.setItem(SIGNUP_METHOD_STORAGE_KEY, method);
  } catch {
    // Signup method is optional analytics metadata.
  }
}

function getSignupMethod(): SignupMethod {
  try {
    const method = sessionStorage.getItem(SIGNUP_METHOD_STORAGE_KEY);
    if (method === "google" || method === "microsoft") return method;
  } catch {
    // Fall back to email when session storage is unavailable.
  }

  return "email";
}

export function trackSignupConversion() {
  const eventParameters = {
    ...getSignupAttribution(),
    method: getSignupMethod(),
  };

  try {
    globalThis.window.gtag?.("event", "sign_up", eventParameters);
  } catch {
    // Analytics failures must never affect a successfully created account.
  } finally {
    removeStoredAttribution();
    removeStoredSignupMethod();
  }
}

export async function trackSignupAfterSuccess<T>(registration: Promise<T>): Promise<T> {
  const result = await registration;
  trackSignupConversion();
  return result;
}
